/**
 *
 * Non-OpenAPI Ajax endpoints
 *
 */

import { PROXY_HEADER } from '@utils/constants';
import { ApiError, OpenAPI } from '@utils/api/cd4pe';
import { getCsrfToken } from '@utils/api/helpers';
import log from 'loglevel';
import { LINKS } from '../../routes';

/**
 * Login API
 */

export type LoginPayload = {
  email: string;
  passwd: string;
};

export type LoginResponse = {
  domain: string;
  redirectTo: string;
  success: boolean;
  username: string;
};

export type AjaxError = {
  error: {
    message: string;
    code: string;
  };
};

export type SignUpPayload = {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  passwd: string;
  passwd2: string;
  companyName?: string;
};

export type SignUpResponse = {
  domain: string;
  redirectTo: string;
  success: boolean;
  username: string;
};

export type DeleteUserPayload = {
  usernameToDelete: string;
};

export type SetSuperUserPayload = {
  username: string;
  superUser: 'true' | 'false';
};

export const login = async (
  path: string,
  payload: LoginPayload,
): Promise<LoginResponse> => {
  const response = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      op: 'PfiLogin',
      content: payload,
    }),
  });

  const body: LoginResponse | AjaxError = await response.json();

  if (response.ok) {
    return body as LoginResponse;
  }

  return 'error' in body
    ? Promise.reject(new Error(body.error?.message))
    : Promise.reject(new Error('Unknown error'));
};

export const performLogin = async (
  payload: LoginPayload,
): Promise<LoginResponse> => login('/cd4pe/login', payload);

export const performRootLogin = async (
  payload: LoginPayload,
): Promise<LoginResponse> => login('/cd4pe/root/login', payload);

export const signUp = async (
  payload: SignUpPayload,
): Promise<SignUpResponse> => {
  const response = await fetch('/cd4pe/signup', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      op: 'PfiSignup',
      content: payload,
    }),
  });

  const body: SignUpResponse | AjaxError = await response.json();

  if (response.ok) {
    return body as SignUpResponse;
  }

  return 'error' in body
    ? Promise.reject(new Error(body.error.message))
    : Promise.reject(new Error('Unknown error'));
};

export const signOut = async (): Promise<boolean> => {
  try {
    const response = await fetch(LINKS.cd4pe.logout, {
      method: 'post',
      headers: new Headers({
        'content-type': 'application/json',
        [PROXY_HEADER.key]: PROXY_HEADER.value,
      }),
      credentials: 'include',
      mode: 'cors',
      body: JSON.stringify({
        op: 'PfiLogout',
        content: null,
      }),
    });

    OpenAPI.HEADERS = {};
    // ok is true if a successful status code was returned
    return response.ok;
  } catch (e) {
    log.error(e);
  }

  return false;
};

export const deleteUser = async (
  payload: DeleteUserPayload,
  loggedInUsername: string,
): Promise<undefined> => {
  const { usernameToDelete } = payload;
  const response = await fetch(`/${loggedInUsername}/ajax`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    credentials: 'include',
    body: JSON.stringify({
      op: 'DeleteUserAccount',
      content: { userName: usernameToDelete },
    }),
  });

  if (response.ok) {
    return undefined;
  }

  const body: AjaxError = await response.json();

  return 'error' in body
    ? Promise.reject(new ApiError(response, body.error.message))
    : Promise.reject(new ApiError(response, 'Unknown error'));
};

export const setSuperUser = async (
  loggedInUsername: string,
  payload: SetSuperUserPayload,
): Promise<undefined> => {
  const response = await fetch(`/${loggedInUsername}/ajax`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken(),
    },
    credentials: 'include',
    body: JSON.stringify({
      op: 'SetSuperUser',
      content: { ...payload },
    }),
  });

  if (response.ok) {
    return undefined;
  }

  const body: AjaxError = await response.json();

  return 'error' in body
    ? Promise.reject(new ApiError(response, body.error.message))
    : Promise.reject(new ApiError(response, 'Unknown error'));
};
